/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// [C35] Installment payment receipt, A4 (closes V-79; D-064, D-108, D-147, D-148). The old application had **no**
// receipt for an installment payment (D07 §1.1): its print button printed the contract statement with the payment
// marked. This is a NEW layout in the frame of the A4 customer entry voucher (`CustomerEntryVoucherA4.tsx`, from
// `CustomerReceiptVoucher.tsx:298-415`): same header, double rule, subject badge, salutation, two-column black-ruled
// table with the tinted amount and balance rows, operator / signature footer — so it reads as one family with the
// voucher the offices already know. Needs the owner's sign-off with the 80 mm voucher (V-95). Every figure arrives
// preformatted from the server's receipt payload (the contract's own paid / remaining, D-108); the amount in words is
// the server's (D-148). Strings are `print.receipt.*` keys; the shared ones reuse `print.entry.*`.
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { PrintOffice } from './StatementA4.js';

export interface InstallmentReceiptA4Props {
  readonly locale: Locale;
  readonly id?: string;
  readonly office: PrintOffice;
  /** `RCT-<number>` (D-147); null while the payment waits for approval. */
  readonly numberLabel: string | null;
  readonly copy: boolean;
  readonly dateLabel: string;
  readonly timeLabel: string;
  readonly customerName: string;
  readonly accountNumber: string | null;
  /** `INS-<number>`. */
  readonly contractNumber: string;
  readonly itemName: string;
  readonly amountLabel: string;
  /** Paid on the contract after this payment, down payment included (D-108). */
  readonly paidTotalLabel: string;
  readonly remainingLabel: string;
  readonly boxLabel: string | null;
  readonly wordsLabel: string | null;
  readonly note: string | null;
  readonly operatorCode: string;
}

export function InstallmentReceiptA4(p: InstallmentReceiptA4Props) {
  const t = createT(p.locale);
  const row = (label: string, value: string, mono = false) => (
    <tr>
      <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{label}</td>
      <td className={`border border-black p-3 ${mono ? 'font-mono font-black' : 'font-bold'} text-center`}>{value}</td>
    </tr>
  );
  return (
    <div
      id={p.id ?? 'installment-receipt'}
      className="bg-white text-black p-8 max-w-[800px] mx-auto font-sans text-right leading-normal print:p-4 print:max-w-none"
      dir={p.locale === 'ar' ? 'rtl' : 'ltr'}
      data-testid="installment-receipt"
    >
      {/* Header */}
      <div className="flex justify-between items-center pb-3">
        <div className="text-right">
          <h1 className="text-lg font-black text-black tracking-tight">{p.office.name}</h1>
          <p className="text-[11px] font-bold text-gray-700 tracking-tight">{t('print.entry.platform_line')}</p>
          <p className="text-[10px] font-bold text-gray-800">{t('print.receipt.department')}</p>
        </div>
        <div className="flex justify-center">{p.office.logoUrl ? <img src={p.office.logoUrl} alt="" className="h-14 w-auto object-contain" /> : null}</div>
        <div className="text-left text-xs font-bold text-black space-y-1" dir="ltr">
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.number')}: </span>
            <span className="font-mono font-black" data-testid="receipt-number">{p.numberLabel ?? '—'}</span>
          </p>
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.date')}: </span>
            <span className="font-mono">{p.dateLabel}</span>
          </p>
          <p className="text-right">
            <span className="font-normal text-gray-700">{t('print.entry.time')}: </span>
            <span className="font-mono">{p.timeLabel}</span>
          </p>
          {p.copy && (
            <p className="text-right">
              <span className="inline-block px-3 py-0.5 bg-rose-50 text-rose-700 font-black rounded-full text-[10px]" data-testid="receipt-copy">
                {t('print.voucher.copy')}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="border-t-2 border-b border-black h-1 my-2"></div>

      <div className="my-6 text-center">
        <div className="inline-block border-2 border-black rounded-2xl bg-gray-100 text-black px-8 py-2 font-black text-sm shadow-sm">
          {t('print.entry.subject_prefix')} {t('print.receipt.subject')}
        </div>
      </div>

      <div className="mb-6 space-y-2">
        <p className="font-black text-sm text-black">
          {t('print.entry.to')} <span className="font-black text-base mr-1 underline">{p.customerName}</span>
          {p.accountNumber && <span className="text-xs text-gray-600 font-mono mr-2">({t('print.entry.account_number')}: {p.accountNumber})</span>}
        </p>
        <p className="text-xs font-bold text-gray-800 leading-relaxed">{t('print.receipt.covering')}</p>
      </div>

      <table className="w-full border-collapse border border-black my-6 text-xs">
        <thead>
          <tr className="bg-gray-100 text-black font-black">
            <th className="border border-black p-3 text-center w-[40%]">{t('print.entry.col_item')}</th>
            <th className="border border-black p-3 text-center w-[60%]">{t('print.entry.col_value')}</th>
          </tr>
        </thead>
        <tbody>
          {row(t('print.receipt.row_number'), p.numberLabel ?? '—', true)}
          {row(t('print.receipt.row_contract'), p.contractNumber, true)}
          {row(t('print.receipt.row_item'), p.itemName)}
          <tr className="bg-blue-50/30">
            <td className="border border-black p-3 font-black bg-blue-100/50 text-center text-blue-950">{t('print.receipt.row_amount')}</td>
            <td className="border border-black p-3 font-mono font-black text-center text-base text-blue-900" data-testid="receipt-amount">{p.amountLabel}</td>
          </tr>
          {row(t('print.receipt.row_paid_total'), p.paidTotalLabel, true)}
          <tr className="bg-emerald-50/30">
            <td className="border border-black p-3 font-black bg-emerald-100/50 text-center text-emerald-950">{t('print.receipt.row_remaining')}</td>
            <td className="border border-black p-3 font-mono font-black text-center text-base text-emerald-900" data-testid="receipt-remaining">{p.remainingLabel}</td>
          </tr>
          {p.boxLabel && row(t('print.entry.row_box'), p.boxLabel)}
          {p.note && row(t('print.entry.row_note'), p.note)}
          {p.wordsLabel && (
            <tr>
              <td className="border border-black p-3 font-bold bg-gray-50/50 text-center">{t('print.receipt.row_words')}</td>
              <td className="border border-black p-3 font-black text-center text-[11px]" data-testid="receipt-words">{p.wordsLabel}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-14 flex justify-between items-start text-xs font-bold px-2">
        <div className="text-right space-y-1">
          <p className="font-black text-black text-sm">{t('print.entry.operator_title')}</p>
          <p className="text-gray-800">
            {t('print.entry.operator_code')}: <span className="font-mono font-black text-sm">{p.operatorCode}</span>
          </p>
          <p className="text-gray-600 text-[11px]">{t('print.receipt.department')}</p>
        </div>
        <div className="text-center space-y-8">
          <p className="font-black text-black text-sm">{t('print.entry.signature')}</p>
          <div className="border-b border-dashed border-black w-56 mx-auto"></div>
        </div>
      </div>
    </div>
  );
}
